import { useEffect } from "react";
import { useNavigate } from "react-router";
import type { AppNotification, ChannelMessage, CommunityStructure, Conversation, DirectMessage, Me, Member, MyCommunity, PresenceEvent } from "@atrium/shared";
import type { NavigateFunction } from "react-router";
import { useMe } from "../features/auth/AuthProvider";
import { getSocket, probeConnection } from "../lib/socket";
import { queryClient } from "../lib/queryClient";
import { patchMessage, removeMessage, upsertMessage, useChatFocus, useTyping } from "../features/chat/cache";
import { applyPollEvent, keepViewerState } from "../features/chat/polls";
import type { Infinite } from "../lib/queryClient";
import { getPrefs } from "../lib/prefs";
import { useForumRealtime } from "../features/forums/realtime";
import { applyPresenceEvent } from "../lib/presence";
import { inQuietHours, playBlip, showDesktopNotification } from "../features/settings/desktopNotify";
import { decryptConversation, decryptDm } from "../features/messages/e2eeMessages";

function describe(n: AppNotification): { title: string; body?: string; path: string } {
  const who = n.actor?.displayName ?? "Someone";
  const snippet = n.preview ?? n.comment?.content ?? n.post?.content ?? undefined;
  const postPath = n.post ? `/p/${n.post.id}` : "/notifications";
  switch (n.type) {
    case "dm":
      return { title: who, body: snippet ?? "Sent you a message", path: n.conversationId ? `/messages/${n.conversationId}` : "/messages" };
    case "mention":
      return {
        title: `${who} mentioned you${n.community ? ` in ${n.community.name}` : ""}`,
        body: snippet,
        path: n.community && n.channelId ? `/c/${n.community.id}/${n.channelId}` : postPath,
      };
    case "follow":
      return { title: `${who} followed you`, path: n.actor ? `/u/${n.actor.username}` : "/notifications" };
    case "like":
      return { title: `${who} liked your post`, body: snippet, path: postPath };
    case "repost":
      return { title: `${who} reposted your post`, body: snippet, path: postPath };
    case "comment":
      return { title: `${who} commented on your post`, body: snippet, path: postPath };
    case "reply":
      return { title: `${who} replied to you`, body: snippet, path: postPath };
    case "community_invite":
      return { title: `${who} invited you to ${n.community?.name ?? "a community"}`, path: n.inviteCode ? `/invite/${n.inviteCode}` : "/notifications" };
    case "community_announcement":
      return { title: n.community?.name ?? "Announcement", body: snippet, path: n.community && n.channelId ? `/c/${n.community.id}/${n.channelId}` : "/notifications" };
    default:
      return { title: "Atrium", body: snippet, path: "/notifications" };
  }
}

/** Desktop notification and/or chime while the app is in the background. */
function alertInBackground(n: AppNotification, navigate: NavigateFunction) {
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  const prefs = getPrefs(queryClient);
  const self = queryClient.getQueryData<Me | null>(["me"]);
  // Do not disturb silences desktop alerts and sounds; the inbox still updates.
  if (self?.presence.status === "dnd") return;
  const typePrefs = self?.settings.notificationPrefs;
  if (typePrefs && typePrefs[n.type] === false) return;
  if (inQuietHours(prefs.quietHours)) return;
  if (prefs.notificationSound) playBlip();
  if (prefs.desktopNotifications) {
    const d = describe(n);
    showDesktopNotification({ title: d.title, body: d.body?.slice(0, 140), tag: n.type === "dm" && n.conversationId ? `dm:${n.conversationId}` : n.id, onClick: () => navigate(d.path) });
  }
}

/**
 * Translates server push events into query-cache updates. One subscription
 * for the whole app; screens simply read from the cache.
 */
export function RealtimeBridge() {
  const me = useMe();
  const navigate = useNavigate();
  useForumRealtime(me.id);

  useEffect(() => {
    const s = getSocket();
    if (!s) return;

    const onMessageNew = (m: ChannelMessage) => {
      // Forum post messages are handled by useForumRealtime and don't make the channel unread.
      if (m.threadId) return;
      upsertMessage(["messages", m.channelId], m);
      useTyping.getState().clear(`c:${m.channelId}`, m.author.id);
      if (m.author.id === me.id) return;
      const viewing = useChatFocus.getState().channelId === m.channelId && document.visibilityState === "visible";
      const mentioned = m.mentionIds.includes(me.id) || m.mentionsEveryone;
      queryClient.setQueryData<CommunityStructure>(["structure", m.communityId], (old) =>
        old
          ? {
              ...old,
              channels: old.channels.map((c) =>
                c.id === m.channelId
                  ? { ...c, lastMessageId: m.id, unread: viewing ? c.unread : true, mentionCount: !viewing && mentioned ? c.mentionCount + 1 : c.mentionCount }
                  : c,
              ),
            }
          : old,
      );
      if (!viewing) {
        queryClient.setQueryData<MyCommunity[]>(["my-communities"], (old) =>
          old?.map((c) => (c.id === m.communityId ? { ...c, unread: true, mentionCount: c.mentionCount + (mentioned ? 1 : 0) } : c)),
        );
      }
    };
    const onMessageUpdate = (m: ChannelMessage) => patchMessage<ChannelMessage>(["messages", m.channelId], m.id, (prev) => keepViewerState(prev, m));
    const onMessagePoll = (p: Parameters<typeof applyPollEvent>[0]) => applyPollEvent(p, me.id);
    const onMessageDelete = (p: { id: string; channelId: string }) => removeMessage(["messages", p.channelId], p.id);
    const onReactions = (p: { id: string; channelId: string; reactions: { emoji: string; count: number }[]; userId: string; emoji: string; added: boolean }) =>
      patchMessage<ChannelMessage>(["messages", p.channelId], p.id, (m) => {
        const mine = new Set(m.reactions.filter((r) => r.me).map((r) => r.emoji));
        if (p.userId === me.id) {
          if (p.added) mine.add(p.emoji);
          else mine.delete(p.emoji);
        }
        return { ...m, reactions: p.reactions.map((r) => ({ ...r, me: mine.has(r.emoji) })) };
      });
    const onChannelTyping = (p: { channelId: string; userId: string; displayName: string }) => {
      if (p.userId !== me.id) useTyping.getState().add(`c:${p.channelId}`, p.userId, p.displayName);
    };
    const onCommunityUpdate = ({ communityId }: { communityId: string }) => {
      void queryClient.invalidateQueries({ queryKey: ["structure", communityId] });
      void queryClient.invalidateQueries({ queryKey: ["members", communityId] });
      // Apps being added or removed change the slash commands and integrations on offer.
      void queryClient.invalidateQueries({ queryKey: ["commands", communityId] });
      void queryClient.invalidateQueries({ queryKey: ["integrations", communityId] });
      void queryClient.invalidateQueries({ queryKey: ["community"] });
      void queryClient.invalidateQueries({ queryKey: ["my-communities"] });
    };
    const onCommunityRemoved = ({ communityId }: { communityId: string }) => {
      queryClient.setQueryData<MyCommunity[]>(["my-communities"], (old) => old?.filter((c) => c.id !== communityId));
      queryClient.removeQueries({ queryKey: ["structure", communityId] });
      void queryClient.invalidateQueries({ queryKey: ["community"] });
      if (window.location.pathname.startsWith(`/c/${communityId}`)) navigate("/c", { replace: true });
    };
    const onPresence = (p: PresenceEvent) => {
      applyPresenceEvent(p);
      const patch = (m: Member): Member => (m.user.id === p.userId ? { ...m, online: p.online, status: p.status, customStatus: p.customStatus, activity: p.activity } : m);
      queryClient.setQueriesData<Infinite<{ items: Member[] }>>({ queryKey: ["members"] }, (old) =>
        old ? { ...old, pages: old.pages.map((pg) => ({ ...pg, items: pg.items.map(patch) })) } : old,
      );
      // Your own choice (e.g. changed on another device) keeps the account menu in sync.
      if (p.userId === me.id) {
        const cur = queryClient.getQueryData<Me | null>(["me"]);
        if (cur && (cur.presence.customStatus?.text !== p.customStatus?.text || cur.presence.activity?.name !== p.activity?.name || cur.presence.customStatus?.emoji !== p.customStatus?.emoji))
          queryClient.setQueryData<Me>(["me"], { ...cur, presence: { ...cur.presence, customStatus: p.customStatus, activity: p.activity } });
      }
    };

    const touchConversation = (convId: string, msg: DirectMessage | null, opts: { unread?: boolean } = {}) => {
      queryClient.setQueryData<Conversation[]>(["conversations"], (old) => {
        if (!old) return old;
        const idx = old.findIndex((c) => c.id === convId);
        if (idx === -1) {
          void queryClient.invalidateQueries({ queryKey: ["conversations"] });
          return old;
        }
        const c = old[idx];
        const updated: Conversation = {
          ...c,
          lastMessage: msg ?? c.lastMessage,
          lastMessageAt: msg?.createdAt ?? c.lastMessageAt,
          unreadCount: opts.unread ? c.unreadCount + 1 : c.unreadCount,
        };
        return [updated, ...old.filter((_, i) => i !== idx)];
      });
    };
    // Encrypted DMs are decrypted on arrival; the cache only ever holds the decrypted form.
    const onDmNew = async (raw: DirectMessage) => {
      const m = await decryptDm(raw);
      upsertMessage(["dm", m.conversationId], m);
      useTyping.getState().clear(`d:${m.conversationId}`, m.author.id);
      const viewing = useChatFocus.getState().conversationId === m.conversationId && document.visibilityState === "visible";
      touchConversation(m.conversationId, m, { unread: m.author.id !== me.id && !viewing });
    };
    const onDmUpdate = async (raw: DirectMessage) => {
      const m = await decryptDm(raw);
      patchMessage<DirectMessage>(["dm", m.conversationId], m.id, () => m);
    };
    const onDmDelete = (p: { id: string; conversationId: string }) => {
      removeMessage(["dm", p.conversationId], p.id);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    };
    const onDmTyping = (p: { conversationId: string; userId: string; displayName: string }) =>
      useTyping.getState().add(`d:${p.conversationId}`, p.userId, p.displayName);
    const onDmRead = (p: { conversationId: string; userId: string; lastReadMessageId: string }) => {
      const patch = (c: Conversation) =>
        c.id === p.conversationId
          ? { ...c, readStates: c.readStates.some((r) => r.userId === p.userId) ? c.readStates.map((r) => (r.userId === p.userId ? { ...r, lastReadMessageId: p.lastReadMessageId } : r)) : c.readStates }
          : c;
      queryClient.setQueryData<Conversation[]>(["conversations"], (old) => old?.map(patch));
      queryClient.setQueryData<Conversation>(["conversation", p.conversationId], (old) => (old ? patch(old) : old));
    };
    const onConversationUpdate = async (raw: Conversation) => {
      const c = await decryptConversation(raw);
      queryClient.setQueryData<Conversation[]>(["conversations"], (old) => {
        if (!old) return old;
        const without = old.filter((x) => x.id !== c.id);
        return [c, ...without].sort((a, b) => (a.lastMessageAt < b.lastMessageAt ? 1 : -1));
      });
      queryClient.setQueryData(["conversation", c.id], c);
    };
    const onNotification = (n: AppNotification) => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      alertInBackground(n, navigate);
    };
    let voiceTimer: ReturnType<typeof setTimeout> | undefined;
    const onVoiceState = () => {
      clearTimeout(voiceTimer);
      voiceTimer = setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["my-communities"] }), 800);
    };
    const onUnread = (u: { notifications: number; conversations: number }) => queryClient.setQueryData(["unread"], u);
    // A staff action or role change: refetch who we are (notices, restriction, staff tools).
    const onMeUpdate = () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["structure"] });
    };
    // Catch up on anything missed. Refetches merge with what's on screen (see
    // mergeMessagePages), so messages that arrive mid-refetch and unsent ones stay put.
    const catchUp = () => {
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      void queryClient.invalidateQueries({ queryKey: ["dm"] });
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
      void queryClient.invalidateQueries({ queryKey: ["unread"] });
      void queryClient.invalidateQueries({ queryKey: ["my-communities"] });
      void queryClient.invalidateQueries({ queryKey: ["structure"] });
      void queryClient.invalidateQueries({ queryKey: ["rail"] }); // layout may have changed on another device
    };
    // After a reconnect the server joins our channel rooms asynchronously and says
    // "ready" when done; syncing before that could miss messages sent in between.
    let pendingCatchUp = false;
    let readyTimer: ReturnType<typeof setTimeout> | undefined;
    const onReconnect = () => {
      pendingCatchUp = true;
      clearTimeout(readyTimer);
      readyTimer = setTimeout(onReady, 5000); // fallback if "ready" never comes
    };
    const onReady = () => {
      clearTimeout(readyTimer);
      if (!pendingCatchUp) return;
      pendingCatchUp = false;
      // A recovered connection was replayed everything it missed; no refetch needed.
      if (s.recovered) return;
      catchUp();
    };
    // Backgrounded tabs (especially on mobile) can sit on a dead socket for a while
    // before it notices: check it on return, and resync after a real absence.
    let hiddenAt = document.visibilityState === "hidden" ? Date.now() : 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (away > 3_000) probeConnection();
      if (away > 15_000 && s.connected) catchUp();
    };
    // The network came back (or changed): the old socket is likely dead.
    const onOnline = () => probeConnection();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    s.on("message:new", onMessageNew);
    s.on("message:update", onMessageUpdate);
    s.on("message:delete", onMessageDelete);
    s.on("message:reactions", onReactions);
    s.on("message:poll", onMessagePoll);
    s.on("channel:typing", onChannelTyping);
    s.on("community:update", onCommunityUpdate);
    s.on("community:removed", onCommunityRemoved);
    s.on("presence", onPresence);
    s.on("dm:new", onDmNew);
    s.on("dm:update", onDmUpdate);
    s.on("dm:delete", onDmDelete);
    s.on("dm:typing", onDmTyping);
    s.on("dm:read", onDmRead);
    s.on("conversation:update", onConversationUpdate);
    s.on("notification:new", onNotification);
    s.on("unread", onUnread);
    s.on("me:update", onMeUpdate);
    s.on("voice:state", onVoiceState);
    s.io.on("reconnect", onReconnect);
    s.on("ready", onReady);
    return () => {
      s.off("message:new", onMessageNew);
      s.off("message:update", onMessageUpdate);
      s.off("message:delete", onMessageDelete);
      s.off("message:reactions", onReactions);
      s.off("message:poll", onMessagePoll);
      s.off("channel:typing", onChannelTyping);
      s.off("community:update", onCommunityUpdate);
      s.off("community:removed", onCommunityRemoved);
      s.off("presence", onPresence);
      s.off("dm:new", onDmNew);
      s.off("dm:update", onDmUpdate);
      s.off("dm:delete", onDmDelete);
      s.off("dm:typing", onDmTyping);
      s.off("dm:read", onDmRead);
      s.off("conversation:update", onConversationUpdate);
      s.off("notification:new", onNotification);
      s.off("unread", onUnread);
      s.off("me:update", onMeUpdate);
      s.off("voice:state", onVoiceState);
      clearTimeout(voiceTimer);
      s.io.off("reconnect", onReconnect);
      s.off("ready", onReady);
      clearTimeout(readyTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [me.id, navigate]);

  return null;
}
